import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { Breadcrumb, Loader } from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";
import {
  PageHeader,
  PageLayout,
} from "@transfr-inc/dashboard-components/layouts";
import { useContainer } from "@src/contexts/container-context";
import { useCurrentUser } from "@queries/account";
import {
  useConnectionProviderLastRun,
  useConnections,
} from "@queries/connection";
import { useProviderIcon } from "@queries/provider-icon";
import { useApiRequest } from "@lib/hooks";
import {
  buildStaticConnectionFromLastRun,
  getStaticIntegrationContext,
} from "@views/integrations/utils";

import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { RootPath } from "@lib/routes.config";
import IntegrationDate from "@views/integrations/list/components/integration-date";
import IntegrationDateWithBadge from "@views/integrations/list/components/integration-date-with-badge";
import SyncHistoryItem from "./sync-history-item";
import "./sync-history.scss";

const SYNC_ITEMS_TO_LOAD = 3;

const SyncHistory = ({ responsiveStyles }) => {
  const history = useHistory();
  const { connectionId, organizationCode } = useParams();
  const { integrationService, organizationService } = useContainer();
  const currentUser = useCurrentUser();
  const [headerLoading, setHeaderLoading] = useState(true);
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [syncsToDisplay, setSyncsToDisplay] = useState([]);

  const { response: organization } = useApiRequest(() =>
    organizationService.getOrganizationDetails(organizationCode)
  );

  const { response: providerList = [] } = useApiRequest(() => {
    return integrationService.getProviders({ includeDisabled: true });
  });

  const { hasStaticConnection, providerDetails } = getStaticIntegrationContext(
    currentUser,
    providerList
  );

  const { data: connections } = useConnections(organizationCode, {
    enabled: !!organizationCode && !hasStaticConnection,
  });
  const connection = connections?.find((conn) => conn.id === connectionId);

  const { data: staticLastRunMapping, isLoading: staticLastRunLoading } =
    useConnectionProviderLastRun(
      providerDetails?.providerName,
      [organizationCode],
      { enabled: hasStaticConnection }
    );

  const staticLastRun = staticLastRunMapping?.[organizationCode] ?? null;

  const effectiveConnection = useMemo(() => {
    if (connection) {
      return connection;
    }

    if (hasStaticConnection && staticLastRun && providerDetails) {
      return buildStaticConnectionFromLastRun(
        staticLastRun,
        providerDetails,
        connectionId
      );
    }

    return {};
  }, [
    connection,
    hasStaticConnection,
    staticLastRun,
    providerDetails?.providerId,
    connectionId,
  ]);

  const { data: icon } = useProviderIcon(effectiveConnection?.providerId, {
    enabled: !!effectiveConnection?.providerId,
  });

  useEffect(() => {
    if (!hasStaticConnection && connection) {
      setHeaderLoading(false);
    } else if (hasStaticConnection) {
      setHeaderLoading(false);
    }
  }, [
    hasStaticConnection,
    connection,
    providerDetails?.connectionId,
    staticLastRunLoading,
    staticLastRun,
    effectiveConnection?.providerId,
    connectionId,
  ]);

  const {
    loading: syncHistoryLoading,
    response: newSyncs = [],
    sendRequest: requestSyncHistory,
  } = useApiRequest(() => {
    // Load one more item to check if there are more items to load
    const itemsToLoad = SYNC_ITEMS_TO_LOAD + 1;

    return integrationService
      .getSyncHistory(
        connectionId,
        syncsToDisplay.length,
        itemsToLoad,
        organizationCode
      )
      .then(async (response) => {
        if (response.length < itemsToLoad) {
          setCanLoadMore(false);
        }

        const result = response.slice(0, SYNC_ITEMS_TO_LOAD);
        setSyncsToDisplay((existingSyncs) => [...existingSyncs, ...result]);

        return result;
      });
  });

  if (headerLoading) {
    return <Loader overlay></Loader>;
  }

  const pendingSync = [
    {
      status: "Pending",
      executionDateTime: new Date().toISOString(),
      message: "Processing...",
      results: [],
    },
  ];

  const syncItems = (
    syncsToDisplay.length > 0 ? syncsToDisplay : pendingSync
  ).map((syncRecord, i) => (
    <SyncHistoryItem
      key={`${i}-${syncRecord.executionDateTime}`}
      syncRecord={syncRecord}
    />
  ));

  return (
    <PageLayout
      className="sync-history"
      responsiveStyles={responsiveStyles}
      header={
        <PageHeader
          breadcrumb={
            <Breadcrumb
              label="All Integrations"
              onClick={() => history.replace(`${RootPath}/integrations`)}
            />
          }
          title={
            <img
              className="provider-image"
              src={icon}
              alt={effectiveConnection.providerName}
            />
          }
          subTitle={
            <div className="connection-info-row">
              <div>{organization?.name}</div>
              <div className="spacer" />
              <div>
                Integrated:{" "}
                <IntegrationDate date={effectiveConnection.createdDate} />
              </div>
              <div className="spacer" />
              <div>
                Last Sync:{" "}
                <IntegrationDateWithBadge connection={effectiveConnection} />
              </div>
            </div>
          }
        ></PageHeader>
      }
    >
      <div className="sync-history__body">
        {syncHistoryLoading && <Loader overlay></Loader>}
        {syncItems}
        {!syncHistoryLoading && canLoadMore && (
          <Button onClick={requestSyncHistory} className="load-more-button">
            Load More
          </Button>
        )}
      </div>
    </PageLayout>
  );
};

export default SyncHistory;
